import { StyleSheet, View, ScrollView, FlatList } from "react-native";
import { useState, useEffect } from "react";
import { useTheme } from "@react-navigation/native";
import { useTranslation } from "react-i18next";
import { createCommonStyles } from "../../styles/commonStyles";
import { useShifts } from '../../hooks/UseShifts';
import CustomLabel from "../../components/CustomLabel/CustomLabel";
import CustomButton from "../../components/CustomButton/CustomButton";
import CustomAlert from "../../components/CustomAlert/CustomAlert";
import ShiftsCreateTemplateScreen from "./ShiftsCreateTemplateScreen";

const ShiftsTemplatesScreen = () => {
    const { colors } = useTheme();
    const { t } = useTranslation();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors);
    const [shiftTemplates, setShiftTemplates] = useState([]);
    const [showShiftsCreateTemplate, setShowShiftsCreateTemplate] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const { getShiftTemplates, deleteShiftTemplate, isLoading, error } = useShifts();

    const formatTime = (value) => {
        if (!value) return '';
        if (typeof value === 'string') {
            return value.length > 5 ? value.slice(0, 5) : value;
        }
        if (value instanceof Date) {
            return value.toTimeString().slice(0, 5);
        }
        return String(value).slice(0, 5);
    };

    useEffect(() => {
        getShiftTemplates().then((res) => {
            setShiftTemplates(res);
        }).catch((err) => {
            console.error(err);
        });
    }, []);

    const handleDelete = (item) => {
        CustomAlert(
            t("common:deleteTemplate"),
            t("common:deleteConfirm"),
            [
                { text: t("common:cancel"), style: 'cancel' },
                {
                    text: t("common:delete"), style: 'destructive',
                    onPress: () => {
                        deleteShiftTemplate(item.id).then((success) => {
                            if (success) {
                                setShiftTemplates((prev) => prev.filter((t) => t.id !== item.id));
                            }
                        });
                    }
                },
            ]
        );
    };

    //#region List of Shift Templates
    const RenderShiftTemplate = ({ item }) => (
        <View style={[common.cardstyle, styles.recordRow]}>
            <CustomLabel
                value={item.tempName}
                type="AVAILABLEUSED"
                fontSize={18}
            />
            <CustomLabel
                value={formatTime(item.startTime)}
                type="AVAILABLEUSED_SECONDARY"
                fontSize={16}
            />
            <CustomLabel
                value={formatTime(item.endTime)}
                type="AVAILABLEUSED_SECONDARY"
                fontSize={16}
            />
            <CustomLabel
                value={`${item.durationMinutes} min`}
                type="AVAILABLEUSED"
                fontSize={16}
            />
            <CustomLabel
                value={item.isOvernight ? 'Yes' : 'No'}
                type="AVAILABLEUSED_SECONDARY"
                fontSize={16}
            />
            <CustomLabel
                value={item.isActive ? 'Yes' : 'No'}
                type="AVAILABLEUSED_SECONDARY"
                fontSize={16}
            />
            <CustomButton
                iconName='pencil-outline'
                onPress={() => { setEditingTemplate(item); setShowShiftsCreateTemplate(true) }}
                show='HOME_BELL'
                type='HOME_BELL'
                textTop='2'
                sz={20}
            />
            <CustomButton
                onPress={() => handleDelete(item)}
                iconName="trash-outline"
                show="HOME_BELL"
                type="HOME_BELL"
                textTop="2"
                sz={20}
            />
        </View>
    );
    //#endregion

    return (
        <View style={common.container}>
            {!showShiftsCreateTemplate && <View style={common.titleButtonRow}>
                <CustomLabel
                    value={t('navigate:shiftTemplate')}
                    type='MINORTITLE'
                    fontSize={20}
                />
                <View style={styles.row}>
                    <CustomButton
                        onPress={() => { setEditingTemplate(null); setShowShiftsCreateTemplate(true) }}
                        text={t('common:createTemplate')}
                        iconName='save-outline'
                        show='BUTTON_SAVE'
                        type='BUTTON_SAVE'
                        isGradient={true}
                        textTop={0}
                        sz={20}
                    />
                </View>
            </View>}
            {showShiftsCreateTemplate ? (
                <ShiftsCreateTemplateScreen
                    item={editingTemplate}
                    onClose={(saved) => {
                        setShowShiftsCreateTemplate(false);
                        setEditingTemplate(null);
                        if (saved) getShiftTemplates().then(setShiftTemplates);
                    }}
                />
            ) :
                <FlatList
                    data={shiftTemplates}
                    keyExtractor={(item) => item.id}
                    ListHeaderComponent={
                        <View style={common.listTitle}>
                            <View style={styles.recordRow}>
                                <CustomLabel value={t("common:tempName")} type="SUBTITLE" fontSize={18} />
                                <CustomLabel value={t("common:startTime")} type="SUBTITLE" fontSize={18} />
                                <CustomLabel value={t("common:endTime")} type="SUBTITLE" fontSize={18} />
                                <CustomLabel value={t("common:duration")} type="SUBTITLE" fontSize={18} />
                                <CustomLabel value={t("common:isOvernight")} type="SUBTITLE" fontSize={18} />
                                <CustomLabel value={t("common:isActive")} type="SUBTITLE" fontSize={18} />
                                <View style={styles.colActions} />
                            </View>
                            <View style={common.divider} />
                        </View>}
                    renderItem={({ item }) => (
                        <RenderShiftTemplate item={item} />
                    )}
                />
            }
        </View>
    );
};

const makeStyles = (colors) =>
    StyleSheet.create({
        emptyContent: {
            flexGrow: 1,
            backgroundColor: colors.background,
        },
        recordRow: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: 12,
            paddingHorizontal: 14,
            zIndex: 10,
        },
        colActions: {
            flexDirection: 'row',
            justifyContent: 'flex-end',
            width: 85,
            alignItems: 'center',
        },
        row: {
            flexDirection: 'row',
            marginHorizontal: 10,
        },
    });

export default ShiftsTemplatesScreen;
